import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Users } from "lucide-react";

import { Avatar, ListGroup, ListRow, Numeric, getInitials, tintForId } from "@/ui";
import { EventCard, type EventCardData } from "@/features/events/EventCard";
import { EventDetailSheet } from "@/features/events/EventDetailSheet";
import { MatchHistoryRow } from "@/features/matches/MatchHistoryRow";
import { ChallengeCard } from "@/features/challenges/ChallengeCard";
import { ChallengeDetailSheet } from "@/features/challenges/ChallengeDetailSheet";
import { ActiveMatchCard } from "@/features/tournaments/ActiveMatchCard";
import {
  useMyActiveMatch,
  useMyChallenges,
  useMyMatches,
  useTournaments,
  useTrainings,
} from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { Challenge } from "@/types/api";

type UpcomingItem =
  | { kind: "event"; date: number; data: EventCardData }
  | { kind: "challenge"; date: number; data: Challenge };

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selected, setSelected] = useState<EventCardData | null>(null);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);

  const tournaments = useTournaments();
  const trainings = useTrainings();
  const matches = useMyMatches(3);
  const challenges = useMyChallenges();
  const activeMatch = useMyActiveMatch();

  const upcoming = useMemo<UpcomingItem[]>(() => {
    const t: UpcomingItem[] = (tournaments.data ?? [])
      .filter((x) => x.status === "registration" || x.status === "in_progress")
      .map((data) => ({
        kind: "event" as const,
        date: new Date(data.date_time).getTime(),
        data: { kind: "tournament" as const, data },
      }));
    const tr: UpcomingItem[] = (trainings.data ?? [])
      .filter((x) => x.status === "active")
      .map((data) => ({
        kind: "event" as const,
        date: new Date(data.date_time).getTime(),
        data: { kind: "training" as const, data },
      }));
    // Active challenges where the user is a participant. "Now" challenges
    // (scheduled_at = null) sort to the top.
    const ch: UpcomingItem[] = (challenges.data ?? [])
      .filter((c) => c.status === "accepted" || c.status === "pending")
      .map((data) => ({
        kind: "challenge" as const,
        date: data.scheduled_at ? new Date(data.scheduled_at).getTime() : 0,
        data,
      }));
    return [...t, ...tr, ...ch]
      .sort((a, b) => a.date - b.date)
      .slice(0, 3);
  }, [tournaments.data, trainings.data, challenges.data]);

  if (!user) return null;

  return (
    <div style={{ paddingBottom: 24 }}>
      {/* Hero: rating + identity */}
      <section
        style={{
          padding: "16px 16px 24px",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <Avatar
          initials={getInitials(user.first_name, user.last_name, user.username)}
          size={56}
          tint={tintForId(user.id)}
          src={user.photo_url}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="caption" style={{ color: "var(--fg3)" }}>Твой рейтинг</div>
          <Numeric size={44} color="var(--brand-orange)">{user.rating}</Numeric>
          <div className="body" style={{ color: "var(--fg2)", marginTop: 4 }}>
            {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || "игрок"}
          </div>
        </div>
      </section>

      {/* Active tournament match — shows above all else when user must play. */}
      {activeMatch.data && <ActiveMatchCard active={activeMatch.data} />}

      {/* Community moved out of the tab bar (its slot is the «Играть» button). */}
      <ListGroup>
        <ListRow
          leading={
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "var(--brand-orange-ghost)",
                color: "var(--brand-orange)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Users size={20} strokeWidth={1.75} />
            </div>
          }
          title="Сообщество"
          subtitle="Рейтинг игроков и поиск по нику"
          trailing={<ChevronRight size={18} color="var(--fg3)" />}
          onClick={() => navigate("/community")}
        />
      </ListGroup>

      {/* Upcoming events */}
      <HeaderWithAction title="Ближайшие события" onAction={() => navigate("/events")} />
      {upcoming.length === 0 ? (
        <EmptyRow text="Новых событий пока нет." />
      ) : (
        upcoming.map((item) =>
          item.kind === "event" ? (
            <EventCard
              key={`event-${item.data.kind}-${item.data.data.id}`}
              event={item.data}
              onClick={() => setSelected(item.data)}
            />
          ) : (
            <ChallengeCard
              key={`challenge-${item.data.id}`}
              challenge={item.data}
              onClick={() => setSelectedChallenge(item.data)}
            />
          ),
        )
      )}

      {/* Recent matches */}
      <HeaderWithAction title="Последние матчи" onAction={() => navigate("/profile/matches")} />
      {matches.data && matches.data.length > 0 ? (
        <div
          style={{
            background: "var(--bg-section)",
            borderRadius: "var(--r-lg)",
            margin: "0 16px",
            overflow: "hidden",
          }}
          className="sp-list-group"
        >
          {matches.data.map((m) => (
            <MatchHistoryRow key={m.id} match={m} />
          ))}
        </div>
      ) : (
        <EmptyRow text="Ещё не сыграл ни одного матча." />
      )}

      <EventDetailSheet event={selected} onClose={() => setSelected(null)} />
      <ChallengeDetailSheet
        challengeId={selectedChallenge?.id ?? null}
        fallback={selectedChallenge ?? undefined}
        onClose={() => setSelectedChallenge(null)}
      />
    </div>
  );
}

function HeaderWithAction({ title, onAction }: { title: string; onAction: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "18px 16px 8px",
      }}
    >
      <span className="section-header">{title}</span>
      <button
        onClick={onAction}
        style={{
          background: "transparent",
          border: 0,
          cursor: "pointer",
          color: "var(--brand-orange)",
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          fontFamily: "var(--font-body)",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        все <ChevronRight size={14} />
      </button>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <div
      style={{
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
        margin: "0 16px 12px",
        padding: "20px 16px",
        color: "var(--fg2)",
        textAlign: "center",
      }}
    >
      {text}
    </div>
  );
}
